/**
 * Hook do detalhe POR CLIENTE do CF por Book (#244) — alimenta o export de
 * conferência da Mesa no card #64. Endpoint computa live (auditoria, fora do
 * hot-path), então cache module-level TTL 10min + startVisiblePolling, padrão
 * da casa (#75). Erro → estado vazio + flag; o consumidor só some a aba extra.
 */
import { useEffect, useState } from "react";
import { getCfBookDetalhe } from "../api/client";
import type { CfBookDetalheResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

const EMPTY: CfBookDetalheResponse = {
  as_of: null,
  linhas: [],
  metadados: {
    universo: "",
    divergencias: {
      netting_cruzado_atacado_varejo: 0,
      sem_carteira_atacado_n: 0,
      sem_carteira_atacado_cf: 0,
      sem_carteira_varejo_n: 0,
      sem_carteira_varejo_cf: 0,
    },
  },
};

interface CacheEntry {
  expiresAt: number;
  promise: Promise<CfBookDetalheResponse>;
}

let cache: CacheEntry | null = null;

export function invalidateCfBookDetalheCache(): void {
  cache = null;
}

export interface UseCfBookDetalheState {
  data: CfBookDetalheResponse;
  isLoading: boolean;
  error: Error | null;
}

export function useCfBookDetalhe(): UseCfBookDetalheState {
  const [state, setState] = useState<UseCfBookDetalheState>({
    data: EMPTY,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: getCfBookDetalhe() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache = null;
          setState({ data: EMPTY, isLoading: false, error: err });
        });
    }

    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
